import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLeadsList } from '../hooks/useLeads';
import { useReservationsList } from '../hooks/useReservations';

type NavigationBadgesContextValue = {
  counts: Record<string, number>;
  setBadgeCount: (key: string, value: number) => void;
};

const NavigationBadgesContext = createContext<NavigationBadgesContextValue | undefined>(undefined);

export function NavigationBadgesProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [leads] = useLeadsList();
  const [reservations] = useReservationsList();

  const setBadgeCount = useCallback((key: string, value: number) => {
    setCounts((prev) => {
      if (value <= 0) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }

      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);

  useEffect(() => {
    const visibleLeadCount = (leads ?? []).filter((lead) => !lead.archived).length;
    setBadgeCount('leads', visibleLeadCount);
  }, [leads, setBadgeCount]);

  useEffect(() => {
    setBadgeCount('reservations', reservations?.length ?? 0);
  }, [reservations, setBadgeCount]);

  const value = useMemo(
    () => ({ counts, setBadgeCount }),
    [counts, setBadgeCount],
  );

  return (
    <NavigationBadgesContext.Provider value={value}>
      {children}
    </NavigationBadgesContext.Provider>
  );
}

export function useNavigationBadges() {
  const context = useContext(NavigationBadgesContext);

  if (!context) {
    throw new Error('useNavigationBadges must be used within NavigationBadgesProvider');
  }

  return context;
}
